import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import Avatar from "../components/Avatar";
import { useNotifs } from "../components/NotifContext";
import { API, typeLabel } from "../api";

function Skeleton({ w = "100%", h = 20, r = 6, mb = 0 }) {
  return (
    <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", marginBottom: mb }} />
  );
}

function DonutChart({ total, used, remaining }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const usedPct = total > 0 ? (used / total) : 0;
  const restPct = total > 0 ? (remaining / total) : 1;
  const usedDash = usedPct * circ;
  const restDash = restPct * circ;
  return (
    <div style={{ position: "relative", width: 140, height: 140, margin: "0 auto 12px" }}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#f0f0f0" strokeWidth="16" />
        {used > 0 && (
          <circle cx="70" cy="70" r={r} fill="none" stroke="#854F0B" strokeWidth="16"
            strokeDasharray={`${usedDash} ${circ - usedDash}`}
            strokeDashoffset={circ / 4}
            style={{ transition: "stroke-dasharray 0.5s" }} />
        )}
        {remaining > 0 && (
          <circle cx="70" cy="70" r={r} fill="none" stroke="#7C6FE0" strokeWidth="16"
            strokeDasharray={`${restDash} ${circ - restDash}`}
            strokeDashoffset={circ / 4 - usedDash}
            style={{ transition: "stroke-dasharray 0.5s" }} />
        )}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#333" }}>{remaining}</div>
        <div style={{ fontSize: 10, color: "#888" }}>restants</div>
      </div>
    </div>
  );
}

function BarChart({ leaves }) {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ label: d.toLocaleDateString("fr-FR", { month: "short" }), year: d.getFullYear(), month: d.getMonth() });
  }
  const counts = months.map(m =>
    leaves.filter(l => {
      const d = new Date(l.date_debut);
      return d.getFullYear() === m.year && d.getMonth() === m.month && l.statut === "approved" && l.type === "CP";
    }).reduce((s, l) => s + parseFloat(l.jours || 0), 0)
  );
  const max = Math.max(...counts, 1);
  return (
    <div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Jours posés — 6 derniers mois</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 80 }}>
        {months.map((m, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 10, color: "#7C6FE0", fontWeight: 700 }}>{counts[i] > 0 ? counts[i] : ""}</div>
            <div style={{ width: "100%", background: counts[i] > 0 ? "linear-gradient(to top, #7C6FE0, #a49ef0)" : "#f0f0f0", borderRadius: "4px 4px 2px 2px", height: Math.max(4, (counts[i] / max) * 60), transition: "height 0.4s" }} />
            <div style={{ fontSize: 10, color: "#bbb" }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniWeekPlanning({ leaves, members }) {
  const today = new Date();
  const toStr = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");

  // Semaine courante (lun → ven)
  const monday = new Date(today);
  monday.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1));
  const days = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { date: d, str: toStr(d), label: d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" }) };
  });

  const absentToday = leaves.filter(l =>
    l.statut === "approved" &&
    l.date_debut.substring(0, 10) <= toStr(today) &&
    l.date_fin.substring(0, 10) >= toStr(today)
  );

  return (
    <div>
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
        Absences cette semaine
      </div>
      {absentToday.length > 0 && (
        <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {absentToday.map(l => (
            <span key={l.id} style={{ display: "flex", alignItems: "center", gap: 5, background: "#E6F1FB", color: "#185FA5", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
              <Avatar prenom={l.prenom || "?"} nom={l.nom || ""} size={16} fontSize={7} />
              {l.prenom} absent{l.statut === "pending" ? " (en attente)" : ""}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
        {days.map(d => {
          const absent = leaves.filter(l =>
            l.statut === "approved" &&
            l.date_debut.substring(0, 10) <= d.str &&
            l.date_fin.substring(0, 10) >= d.str
          );
          const isToday = d.str === toStr(today);
          return (
            <div key={d.str} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, color: isToday ? "#7C6FE0" : "#bbb", fontWeight: isToday ? 700 : 400, marginBottom: 4 }}>
                {d.label}
              </div>
              <div style={{
                height: 36, borderRadius: 6, background: absent.length > 0 ? "#B5D4F4" : isToday ? "#EDE9FF" : "#f5f5f7",
                border: `1px solid ${isToday ? "#c5b8f8" : "#ebebeb"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {absent.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#185FA5" }}>{absent.length}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {absentToday.length === 0 && (
        <div style={{ fontSize: 12, color: "#bbb", marginTop: 6, textAlign: "center" }}>Personne n'est absent aujourd'hui 🎉</div>
      )}
    </div>
  );
}

function PieChart({ leaves }) {
  const palette = ["#7C6FE0","#185FA5","#3B6D11","#854F0B","#A32D2D","#5C8A8A"];
  const approved = leaves.filter(l => l.statut === "approved" && l.type !== "Absence");
  const types = [...new Set(approved.map(l => l.type))];
  const data = types.map((t, i) => ({
    t, color: palette[i % palette.length],
    v: approved.filter(l => l.type === t).reduce((s, l) => s + parseFloat(l.jours || 0), 0)
  })).filter(d => d.v > 0);
  const total = data.reduce((s, d) => s + d.v, 0);
  if (total === 0) return null;
  const r = 36; const cx = 48; const cy = 48; const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <svg width={96} height={96}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f0" strokeWidth={12} />
        {data.map((d, i) => {
          const dash = (d.v / total) * circ;
          const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={12}
            strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={circ / 4 - offset} />;
          offset += dash;
          return el;
        })}
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#333">{total}j</text>
      </svg>
      <div>
        {data.map(d => (
          <div key={d.t} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: "#555" }}>{typeLabel(d.t)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#333", marginLeft: "auto" }}>{d.v}j</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, token } = useAuth();
  const { push: pushNotif } = useNotifs();
  const [balance, setBalance] = useState({ cp_restants: 0, cp_total: 30 });
  const [leaves, setLeaves] = useState([]);
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [globalStats, setGlobalStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    const isManager = user.role === "associe" || user.role === "daf";

    Promise.all([
      axios.get(API + "/leaves/mine", { headers }),
      axios.get(API + "/users/balance", { headers }),
      isManager ? axios.get(API + "/leaves/team", { headers }) : Promise.resolve({ data: [] }),
      isManager ? axios.get(API + "/users/team", { headers }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]).then(([leavesRes, balanceRes, teamRes, membersRes]) => {
      const myLeaves = leavesRes.data;
      setLeaves(myLeaves);
      const total = parseFloat(balanceRes.data.cp_total) || 30;
      const poses = myLeaves.filter(l => l.statut === "approved" && l.type === "CP").reduce((s, l) => s + parseFloat(l.jours || 0), 0);
      setBalance({ cp_restants: total - poses, cp_total: total });

      if (isManager) {
        setTeamLeaves(teamRes.data);
        setTeamMembers(membersRes.data);
        setPendingCount(teamRes.data.filter(l => l.statut === "pending" || l.cancel_requested).length);
      }
      setLoading(false);
    });

    if (user.role === "daf") {
      Promise.all([
        axios.get(API + "/users", { headers }),
        axios.get(API + "/leaves/cabinet", { headers }),
      ]).then(([usersRes, leavesRes]) => {
        const today = new Date().toISOString().split("T")[0];
        const allLeaves = leavesRes.data;
        const allUsers = usersRes.data;
        const collabs = allUsers.filter(u => u.role === "collab" || u.role === "assistante");
        const absentToday = allLeaves.filter(l =>
          l.statut === "approved" &&
          l.date_debut.substring(0, 10) <= today &&
          l.date_fin.substring(0, 10) >= today
        ).length;
        const pendingAll = allLeaves.filter(l => l.statut === "pending" || l.cancel_requested).length;
        const lowBalance = collabs.filter(u => parseFloat(u.cp_restants || 30) <= 5).length;
        const posedThisMonth = allLeaves.filter(l => {
          const d = new Date(l.date_debut);
          const now = new Date();
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && l.statut === "approved" && l.type === "CP";
        }).reduce((s, l) => s + parseFloat(l.jours || 0), 0);
        setGlobalStats({ total: collabs.length, absentToday, pendingAll, lowBalance, posedThisMonth });
      });
    }
  }, [token, user.role]);

  const poses = balance.cp_total - balance.cp_restants;
  const upcomingLeaves = leaves
    .filter(l => new Date(l.date_debut) >= new Date() && l.statut !== "refused" && l.type !== "Absence")
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));

  const isManager = user.role === "associe" || user.role === "daf";

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Bonjour";
    if (h < 18) return "Bon après-midi";
    return "Bonsoir";
  };

  if (loading) return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ marginBottom: 24 }}>
        <Skeleton w={240} h={28} r={8} mb={8} />
        <Skeleton w={180} h={16} r={6} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[1, 2, 3].map(i => <Skeleton key={i} h={80} r={10} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Skeleton h={200} r={12} />
        <Skeleton h={200} r={12} />
      </div>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
    </div>
  );

  // Vue DAF
  if (user.role === "daf" && globalStats) {
    return (
      <div style={{ animation: "fadeIn 0.3s ease" }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: "#1a1a2e" }}>{greeting()} {user.prenom} 👋</h2>
          <div style={{ fontSize: 13, color: "#aaa", marginTop: 4 }}>{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </div>

        {pendingCount > 0 && (
          <div onClick={() => navigate("/validations")}
            style={{ background: "linear-gradient(135deg,#FAEEDA,#fff8ef)", border: "1px solid #F5C97A", borderRadius: 10, padding: "14px 18px", marginBottom: 20, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 8px rgba(245,201,122,0.2)" }}>
            <span style={{ background: "#854F0B", color: "#fff", borderRadius: "50%", width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{pendingCount}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#854F0B" }}>Demande{pendingCount > 1 ? "s" : ""} en attente de validation</div>
              <div style={{ fontSize: 12, color: "#a0660a" }}>Cliquer pour traiter →</div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Collaborateurs", value: globalStats.total, color: "#7C6FE0", bg: "#EEEDFE", icon: "👥" },
            { label: "Absents aujourd'hui", value: globalStats.absentToday, color: "#185FA5", bg: "#E6F1FB", icon: "🏖️" },
            { label: "Demandes en attente", value: globalStats.pendingAll, color: "#854F0B", bg: "#FAEEDA", icon: "⏳" },
            { label: "Soldes faibles", value: globalStats.lowBalance, color: "#A32D2D", bg: "#FCEBEB", icon: "⚠️" },
            { label: "Jours posés ce mois", value: globalStats.posedThisMonth, color: "#3B6D11", bg: "#EAF3DE", icon: "📊" },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "📅 Planning cabinet", path: "/planning-global", color: "#7C6FE0", bg: "#EEEDFE" },
            { label: "⚙️ Administration", path: "/admin", color: "#555", bg: "#f5f5f5" },
            { label: "📊 Statistiques", path: "/statistics", color: "#185FA5", bg: "#E6F1FB" },
            { label: "📜 Journal", path: "/audit", color: "#555", bg: "#f5f5f5" },
          ].map(b => (
            <button key={b.path} onClick={() => navigate(b.path)}
              style={{ background: b.bg, color: b.color, border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              {b.label}
            </button>
          ))}
        </div>
        <style>{`@keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }`}</style>
      </div>
    );
  }

  return (
    <div style={{ animation: "fadeIn 0.3s ease", maxWidth: 1400 }}>
      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: "#1a1a2e" }}>{greeting()} {user.prenom} 👋</h2>
        <div style={{ fontSize: 13, color: "#aaa", marginTop: 4 }}>{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      </div>

      {/* Alerte validation manager */}
      {isManager && pendingCount > 0 && (
        <div onClick={() => navigate("/validations")}
          style={{ background: "linear-gradient(135deg,#FAEEDA,#fff8ef)", border: "1px solid #F5C97A", borderRadius: 10, padding: "14px 18px", marginBottom: 20, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 8px rgba(245,201,122,0.2)" }}>
          <span style={{ background: "#854F0B", color: "#fff", borderRadius: "50%", width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>{pendingCount}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#854F0B" }}>Demande{pendingCount > 1 ? "s" : ""} en attente de validation</div>
            <div style={{ fontSize: 12, color: "#a0660a" }}>Cliquer pour traiter →</div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Dotation annuelle", value: balance.cp_total + " j", color: "#7C6FE0", bg: "#EEEDFE", icon: "📋", sub: "jours alloués" },
          { label: "Jours posés", value: poses + " j", color: "#854F0B", bg: "#FAEEDA", icon: "✈️", sub: "cette année" },
          { label: "Solde restant", value: balance.cp_restants + " j", color: balance.cp_restants <= 5 ? "#A32D2D" : "#3B6D11", bg: balance.cp_restants <= 5 ? "#FCEBEB" : "#EAF3DE", icon: balance.cp_restants <= 5 ? "⚠️" : "✅", sub: balance.cp_restants <= 5 ? "Attention solde faible" : "disponibles" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>{k.icon}</span>
              <span style={{ fontSize: 11, color: k.color, fontWeight: 600, background: "rgba(255,255,255,0.5)", padding: "2px 8px", borderRadius: 10 }}>{k.label}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>{k.value}</div>
            <div style={{ fontSize: 11, color: k.color, marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Graphiques */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16, marginBottom: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Utilisation du solde</div>
          <DonutChart total={balance.cp_total} used={poses} remaining={balance.cp_restants} />
          <div style={{ display: "flex", justifyContent: "center", gap: 16, fontSize: 11, color: "#666" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, background: "#7C6FE0", borderRadius: 2, display: "inline-block" }} /> Restants
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, background: "#854F0B", borderRadius: 2, display: "inline-block" }} /> Posés
            </span>
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <BarChart leaves={leaves} />
          {upcomingLeaves.length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid #f5f5f5", paddingTop: 12 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Prochains congés</div>
              {upcomingLeaves.slice(0, 3).map(l => {
                const cfgColor = l.statut === "approved" ? "#3B6D11" : "#854F0B";
                const cfgBg = l.statut === "approved" ? "#EAF3DE" : "#FAEEDA";
                return (
                  <div key={l.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, fontSize: 12 }}>
                    <span style={{ color: "#555" }}>
                      {new Date(l.date_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} → {new Date(l.date_fin).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                      <span style={{ color: "#bbb", marginLeft: 6 }}>{l.type}</span>
                    </span>
                    <span style={{ background: cfgBg, color: cfgColor, padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                      {l.statut === "approved" ? "✅" : "⏳"} {parseFloat(l.jours)}j
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Planning semaine + camembert côte à côte (empilés sur mobile) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16, marginBottom: 16 }}>
        {/* Mini planning semaine (managers) */}
        {isManager && teamLeaves.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <MiniWeekPlanning leaves={teamLeaves} members={teamMembers} />
          </div>
        )}

        {/* Camembert types */}
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>Répartition par type</div>
          <PieChart leaves={leaves} />
          {leaves.filter(l => l.statut === "approved" && l.type !== "Absence").length === 0 && (
            <div style={{ fontSize: 12, color: "#ccc", textAlign: "center", padding: 8 }}>Aucun congé approuvé</div>
          )}
        </div>
      </div>

      {/* CTA */}
      <button onClick={() => navigate("/demande")}
        style={{ background: "linear-gradient(135deg, #7C6FE0, #185FA5)", color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(124,111,224,0.35)", display: "flex", alignItems: "center", gap: 8 }}>
        {isManager ? "✏️ Saisir mes congés" : "➕ Nouvelle demande"}
      </button>

      <style>{`@keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
}

export default Dashboard;
